import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createSkuSchema, formatMoney } from '@lombok/shared';
import type { CreateSkuInput } from '@lombok/shared';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { QueryState } from '@/components/QueryState';
import { CheckboxField, FormActions, FormError, TextField } from '@/components/form';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { useCreateSku, useSkus } from '@/data/hooks';
import { parseDecimal } from '@/lib/decimal';

/**
 * SKUs (presentaciones) for a product, with an inline create form (admin). SKU
 * base prices are entered in whole USD and stored in minor units server-side;
 * the list shows the persisted price via the shared formatMoney helper.
 */
export function ProductSkusCard({ productId }: { productId: string }) {
  const query = useSkus(productId);
  const isAdmin = useIsAdmin();
  const [adding, setAdding] = useState(false);
  const items = query.data ?? [];

  return (
    <Card>
      {/* Title centered, action on the same line at the right (the empty first column balances it). */}
      <CardHeader className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <span aria-hidden="true" />
        <CardTitle className="text-center">Presentaciones (SKUs)</CardTitle>
        {!adding && isAdmin ? (
          <Button size="sm" variant="outline" className="justify-self-end" onClick={() => setAdding(true)}>
            Agregar SKU
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pt-0">
        {adding ? (
          <SkuCreateForm productId={productId} onDone={() => setAdding(false)} />
        ) : null}

        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
          isEmpty={items.length === 0}
          loadingLabel="Cargando presentaciones…"
          emptyTitle="Sin presentaciones"
          emptyDescription="Este producto aún no tiene SKUs. Agrega uno para poder venderlo."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                  <th className="px-3 py-2 font-medium">Código</th>
                  <th className="px-3 py-2 font-medium">Nombre</th>
                  <th className="px-3 py-2 font-medium">Unidad</th>
                  <th className="px-3 py-2 text-right font-medium">Precio base</th>
                  <th className="px-3 py-2 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.skuId} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-mono text-xs text-text-primary">{s.code}</td>
                    <td className="px-3 py-2 text-text-primary">{s.name}</td>
                    <td className="px-3 py-2 text-text-muted">{s.unit}</td>
                    <td className="px-3 py-2 text-right font-medium text-text-primary">
                      {formatMoney(s.basePriceMinorUsd)}
                    </td>
                    <td className="px-3 py-2 text-text-muted">{s.active ? 'Activo' : 'Inactivo'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </QueryState>
      </CardContent>
    </Card>
  );
}

function SkuCreateForm({ productId, onDone }: { productId: string; onDone: () => void }) {
  const mutation = useCreateSku(productId);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateSkuInput>({
    resolver: zodResolver(createSkuSchema),
    defaultValues: {
      productId,
      code: '',
      name: '',
      unit: '',
      basePriceUsd: 0,
      active: true,
    },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, { onSuccess: () => onDone() });
  });

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4 rounded-md border border-border p-4" noValidate>
      <FormError error={mutation.error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="Código"
          required
          placeholder="VAL-AC-20W50-1QT"
          error={errors.code?.message}
          {...register('code')}
        />
        <TextField
          label="Nombre"
          required
          placeholder="All-Climate 20W-50 (1 qt)"
          error={errors.name?.message}
          {...register('name')}
        />
        <TextField
          label="Unidad"
          required
          placeholder="caja, litro, tambor…"
          error={errors.unit?.message}
          {...register('unit')}
        />
        <TextField
          label="Precio base (USD)"
          inputMode="decimal"
          required
          placeholder="Ej. 12,50"
          hint="Monto en dólares. El sistema lo guarda en centavos."
          error={errors.basePriceUsd?.message}
          {...register('basePriceUsd', { setValueAs: parseDecimal })}
        />
      </div>
      <CheckboxField label="Activo" {...register('active')} />
      <FormActions>
        <Button type="button" variant="outline" onClick={onDone}>
          Cancelar
        </Button>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Guardando…' : 'Crear SKU'}
        </Button>
      </FormActions>
    </form>
  );
}
